import {roomDimensionEvidenceRows} from '../engine/roomDimensionEvidence.js';
import { wallJoinEvidenceRows } from '../engine/wallJoinEvidence.js';
import { wallHeightEvidenceRows } from '../engine/wallHeightEvidence.js';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import Modal from './Modal.jsx';
import { reviewFieldEvidence } from '../engine/fieldEvidenceReview.js';
import { siteLevels } from '../engine/siteBuildings.js';
import { formatLength } from '../engine/units.js';
import { buildFieldEvidenceReport } from '../engine/fieldEvidenceReport.js';
import { quadrilateralEvidenceRows } from '../engine/quadrilateralEvidence.js';
import { wallSplitEvidenceRows } from '../engine/wallSplitEvidence.js';
import { measurementCheckEvidenceRows } from '../engine/measurementCheckEvidence.js';
import { fieldReferenceEvidenceRows } from '../engine/fieldReferenceEvidence.js';
import { nativeCapabilities, requestNative } from '../engine/nativeCapture.js';

const PAGE_SIZE = 30;
const labels = { info: 'Recorded information', warning: 'Needs review', error: 'Cannot evaluate' };
const sources = { manual: 'Manual entry', lidar: 'LiDAR', bluetooth: 'Bluetooth' };

/** Read-only review of the canonical Site, including inactive building floors.
 * Opening a floor uses navigation only: it never creates a save or Undo entry. */
export default function FieldEvidenceReview({ store, onClose, onCheckFloor, returnFocusRef, disabled, projectName, revision, saveState, accountID }) {
  const [building, setBuilding] = useState('');
  const [floor, setFloor] = useState('');
  const [filter, setFilter] = useState('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [error, setError] = useState('');
  const nextRef = useRef(null);
  const focusNext = useRef(false);
  const errorRef = useRef(null);
  const [printing, setPrinting] = useState(false);
  const printLatch = useRef(false), printGeneration = useRef(0), live = useRef(false);
  const contextKey = `${accountID || ''}/${store.projectId || ''}`, currentContext = useRef(contextKey);
  currentContext.current = contextKey;
  useEffect(() => {
    live.current = true; printGeneration.current++; printLatch.current = false; setPrinting(false); setError('');
    return () => { live.current = false; printGeneration.current++; };
  }, [contextKey]);
  const report = useMemo(() => {
    try { return reviewFieldEvidence(store.toDocument()); }
    catch { return null; }
  }, [store, store.rev]);
  const floors = report?.floors || [];
  const buildings = [...new Map(floors.filter(f => f.buildingID).map(f => [f.buildingID, f.buildingName])).entries()];
  const choices = floors.filter(f => f.floorID && (!building || f.buildingID === building));
  const items = (report?.items || []).filter(item => (!building || item.buildingID === building)
    && (!floor || item.floorID === floor) && (filter !== 'attention' || item.severity !== 'info')
    && (filter !== 'assumptions' || item.assumption === true));
  const summary = report?.summary;
  useEffect(() => {
    if (building && !buildings.some(([id]) => id === building)) { setBuilding(''); setFloor(''); setLimit(PAGE_SIZE); }
    else if (floor && !choices.some(f => f.floorID === floor)) { setFloor(''); setLimit(PAGE_SIZE); }
  }, [report, building, floor]);
  useLayoutEffect(() => { if (focusNext.current) { nextRef.current?.focus(); focusNext.current = false; } }, [limit]);
  useEffect(() => { if (error) errorRef.current?.focus(); }, [error]);
  function update(setter, value) { setter(value); setLimit(PAGE_SIZE); setError(''); }
  async function printReview() {
    if (printLatch.current || disabled || !report) return;
    const generation = printGeneration.current, context = currentContext.current;
    const isCurrent = () => live.current && generation === printGeneration.current && context === currentContext.current;
    printLatch.current = true; setPrinting(true); setError('');
    let popup;
    try {
      const output = buildFieldEvidenceReport(store.toDocument(), { projectName, revision, dirty: store.dirty, saveState,
        generatedAt: new Date().toISOString(), scope: { buildingID: building, floorID: floor, filter } });
      if (nativeCapabilities()?.printReport) {
        const result = await requestNative('printReport', { html: output.html });
        if (result?.error) throw new Error(result.error);
      } else {
        popup = window.open('', '_blank');
        if (!popup) throw new Error('Allow pop-up windows to print this review or save it as PDF.');
        popup.opener = null;
        popup.document.write(output.html); popup.document.close();
        // The popup may background the editor and suspend its animation frames.
        // Give the report a frame, with a timer fallback for a background popup.
        await new Promise(resolve => {
          const finish = () => { clearTimeout(timer); resolve(); };
          const timer = setTimeout(finish, 100);
          popup.requestAnimationFrame(finish);
        });
        if (!isCurrent()) { popup.close(); return; }
        if (popup.closed) throw new Error('The report window closed before printing. Try again.');
        popup.focus(); popup.print();
      }
    } catch (e) {
      if (isCurrent()) { setError(e.message); if (error === e.message) errorRef.current?.focus(); }
      popup?.close();
    } finally { if (isCurrent()) { printLatch.current = false; setPrinting(false); } }
  }
  function openFloor(item, check = false) {
    try {
      if (disabled || printing) throw new Error('Wait for the current operation to finish before opening a floor.');
      const current = siteLevels(store.toDocument()).filter(level => level.id === item.floorID && level.buildingId === item.buildingID);
      if (current.length !== 1) throw new Error('This floor changed or cannot be identified. Close and reopen the review.');
      store.switchFloor(item.floorID, { viewOnly: true });
      if (check) onCheckFloor(); else onClose();
    } catch (e) { setError(e.message); if (error === e.message) errorRef.current?.focus(); }
  }
  return <Modal open className="room-shell-dialog" width={760} focusKey="evidence-review" returnFocusRef={returnFocusRef} onClose={() => { if (!printing) onClose(); }}>
    <div className="dhead"><h2>Site field evidence</h2></div>
    <div className="dbody design-fields" style={{ overflowWrap: 'anywhere' }}>
      <p>Review measurements and drawing assumptions across all buildings, including upper floors and basements. This uses the current plan, including unsaved edits.</p>
      <p className="hint">A passing distance check applies only to its recorded endpoints and your chosen limit. It does not certify the floor, instrument or building accuracy. Calculated dimensions and assumed directions still need independent field checks.</p>
      {!report && <p role="alert">The Site evidence could not be read. Keep the original plan and review its floors individually.</p>}
      {summary && <section aria-label="Site evidence summary">
        <p><strong>{summary.floors} floors</strong> · {summary.checks.recorded} recorded distance checks · {summary.checks.withinTolerance} within their limits</p>
        <p>{summary.attention} findings need review · {summary.assumptions} recorded assumptions</p>
        {!report.complete && <p role="alert">This review is incomplete. Some evidence could not be evaluated; inspect the findings before relying on the summary.</p>}
      </section>}
      <div className="design-fields">
        <label>Building<select disabled={printing} value={building} onChange={e => { update(setBuilding, e.target.value); setFloor(''); }}>
          <option value="">All buildings</option>{buildings.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
        </select></label>
        <label>Floor<select disabled={printing} value={floor} onChange={e => update(setFloor, e.target.value)}>
          <option value="">All floors</option>{choices.map(f => <option key={f.floorID} value={f.floorID}>{f.buildingName} · {f.floorName}</option>)}
        </select></label>
        <label>Show<select disabled={printing} value={filter} onChange={e => update(setFilter, e.target.value)}>
          <option value="all">All findings</option><option value="attention">Needs review</option><option value="assumptions">Assumptions</option>
        </select></label>
      </div>
      <p role="status">{items.length} findings in this view. Showing {Math.min(limit, items.length)}.</p>
      <p className="hint">Printing includes all {items.length} matching findings, including those below, with the current Site revision and save status.</p>
      {printing && <p role="status">Preparing the report. Finish or cancel the print dialog to continue.</p>}
      {report && !items.length && <p>No findings match these filters. This does not establish field accuracy.</p>}
      <div aria-label="Field evidence findings" className="design-fields">
        {items.slice(0, limit).map((item, index) => <section key={item.id} className="design-card" aria-label={item.title}>
          <h3 ref={index === limit - PAGE_SIZE ? nextRef : undefined} tabIndex={-1}>{item.title}</h3>
          <p><strong>{labels[item.severity] || 'Needs review'}</strong> · {item.buildingName} · {item.floorName}</p>
          <p>{item.detail}</p>
          {(sources[item.measurementSource] || Number.isFinite(Date.parse(item.capturedAt))) && <p className="hint">
            {sources[item.measurementSource] && <>Source: {sources[item.measurementSource]} · </>}
            {Number.isFinite(Date.parse(item.capturedAt)) ? new Date(item.capturedAt).toLocaleString() : 'Capture time unavailable'}
          </p>}
          {Number.isFinite(item.measuredMetres) && <p>Recorded: {formatLength(item.measuredMetres, store.doc, 3)}
            {Number.isFinite(item.appliedMetres) && <> · {item.splitOriginal?'Original whole-wall applied length':'Applied centreline length'}: {formatLength(item.appliedMetres, store.doc, 3)}</>}
            {Number.isFinite(item.planMetres) && <> · {item.splitOriginal?'Current combined piece length':'Current plan'}: {formatLength(item.planMetres, store.doc, 3)}</>}
            {Number.isFinite(item.errorMetres) && <> · {Number.isFinite(item.appliedMetres) ? 'Difference from applied length' : 'Difference'}: {formatLength(item.errorMetres, store.doc, 3)}</>}
            {Number.isFinite(item.toleranceMetres) && <> · Allowed difference: {formatLength(item.toleranceMetres, store.doc, 3)}</>}
          </p>}
          {item.roomDimensions && <details><summary>Original room dimensions and wall thickness</summary>
            {roomDimensionEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.joinHistory && <details><summary>Retained split and rejoin history</summary>
            {wallJoinEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.splitOriginal && <details><summary>Original whole wall and retained pieces</summary>
            {wallSplitEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.wallHeight && <details><summary>Original wall height entry</summary>
            {wallHeightEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.checkReferences && <details><summary>Original distance-check corners</summary>
            {measurementCheckEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.reference && <details><summary>Reference coordinates and original construction</summary>
            {fieldReferenceEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.outlineLengths && <details><summary>Original outline distances and sources</summary>
            {quadrilateralEvidenceRows(item,store.doc).map(([name,value])=><p key={name}><strong>{name}:</strong> {value}</p>)}
          </details>}
          {item.floorID && <div className="btnrow">
            <button className="btn" disabled={disabled || printing} onClick={() => openFloor(item)}>Open floor</button>
            {item.kind === 'measurement-check' && <button className="btn" disabled={disabled || printing} onClick={() => openFloor(item, true)}>Review distance checks</button>}
          </div>}
        </section>)}
      </div>
      {limit < items.length && <button className="btn" onClick={() => { focusNext.current = true; setLimit(value => value + PAGE_SIZE); }}>Show next {Math.min(PAGE_SIZE, items.length - limit)} findings</button>}
      {error && <p ref={errorRef} tabIndex={-1} role="alert">{error}</p>}
    </div>
    <div className="dfoot" style={{ flexWrap: 'wrap' }}><button className="btn" disabled={printing} onClick={onClose}>Close</button><button className="btn primary" disabled={printing || disabled || !report} onClick={printReview}>Print / save review as PDF</button></div>
  </Modal>;
}
